import { useState as useHookState } from '@hookstate/core';

import { AssetContext } from '@pages/Asset/Asset.query';
import { useContext } from 'react';

import { ITooltip } from '@components';
import { DocumentsItemMenu } from '@components/AssetPageMenus/DocumentsItemMenu/DocumentsItemMenu';
import { DocumentsMenu } from '@components/AssetPageMenus/DocumentsMenu/DocumentsMenu';
import { ResourceLink } from '@components/ResourceLink/ResourceLink';
import { NavigationContext } from '@layout/navigation/navigation';
import { patchDocument } from '@services/queries/documents';
import { getPermissionData } from '@utils';
import { useMutation, useQueryClient } from 'react-query';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { checkDevice } from '@store/redux/features/mobile/mobileSelectors';
import { AddDocuments } from './Components/AddDocuments/AddDocuments';
import { EditDocument } from './Components/EditDocument/EditDocument';
import { NoDocuments } from './Components/NoDocuments/NoDocuments';

import { Search } from '../Search/Search';
import { CountLoading, DocumentsLoading } from './Documents.loading';
import {
  Container,
  Counter,
  CounterWrapper,
  DItems,
  Head,
  Row,
  Title,
} from './Documents.styles';
import { fixCannonUrl, handleClick } from './Documents.utils';
import {
  makeShortcutRefCallback,
  shortcuthashes,
} from '../Sidebar/components/Shortcuts/Shortcuts.utils';
import { NoFeatureMenu } from '../Updates/Components/NoFeatureMenu/NoFeatureMenu';

export const Documents = () => {
  const params = useParams<{ equipmentId: string }>();

  const {
    equipmentQuery,
    openAddDocument,
    openEditDocument,
    documentsQuery,
    sortDocumentByFavorite,
    shortcutRefs,
    selectedDocument,
  } = useContext(AssetContext);

  const { role } = useContext(NavigationContext);

  const equipment = equipmentQuery?.data;

  const favoritePermission = getPermissionData({
    owner: true,
    action: 'favorite',
    role,
    type: 'Document',
  });

  const queryClient = useQueryClient();

  const mutationPatchDocument = useMutation(patchDocument, {
    onSuccess: (_, { data: { documentId, favorite } }) => {
      if (documentsQuery?.data) {
        const documentsData = documentsQuery.data;

        const documentIndex = documentsData.findIndex(
          item => item.id === documentId,
        );

        documentsData[documentIndex].favorite = !!favorite;

        queryClient.setQueryData(
          ['equipments', params.equipmentId, 'documents'],
          documentsData,
        );
      }

      queryClient.invalidateQueries([
        'equipments',
        params.equipmentId,
        'documents',
      ]);
    },
  });

  const search = useHookState<string>('');

  const { isDesktop, isMobile } = useSelector(checkDevice());
  if (
    !documentsQuery ||
    !documentsQuery.data ||
    documentsQuery.isLoading ||
    documentsQuery.isError
  ) {
    return <DocumentsLoading />;
  }

  const containerState = documentsQuery.data.length === 0 ? 'empty' : '';

  const count = () => {
    return documentsQuery?.data?.length || 0;
  };

  const isCountLoading = () => {
    return documentsQuery?.isLoading;
  };

  const refCallback = makeShortcutRefCallback(
    shortcutRefs,
    shortcuthashes.documents,
  );
  return (
    <Container data-state={containerState} ref={refCallback}>
      <Head>
        <CounterWrapper>
          <Title>Documents</Title>
          {isCountLoading() ? <CountLoading /> : <Counter>{count()}</Counter>}
        </CounterWrapper>
        <Row className="icons">
          {isDesktop && (
            <Search loadSearch={search.set} helperText="Find a document" />
          )}
          {isDesktop && <DocumentsMenu />}
          {isMobile && <NoFeatureMenu />}
        </Row>
      </Head>

      <DItems hidden={containerState === 'empty'}>
        {documentsQuery.data
          .filter(item =>
            item.label.toLowerCase().includes(search.get().toLowerCase()),
          )
          .sort((a, b) => {
            if (sortDocumentByFavorite.get()) {
              return Number(b.favorite) - Number(a.favorite);
            }
            return 0;
          })
          .map(_item => {
            const item = fixCannonUrl(_item);
            return (
              <ResourceLink
                key={_item.id}
                data-for="document-link-tip"
                onStarClick={handleClick(
                  favoritePermission,
                  mutationPatchDocument,
                  item,
                )}
                isFavorite={item.favorite}
                showSourceOrDesc={!!item.description}
                label={item.label}
                uri={item.uri ?? ''}
                sourceOrDesc={item.description}
                menu={isDesktop && <DocumentsItemMenu document={item} />}
              />
            );
          })}
        <ITooltip id="document-link-tip" place="bottom" />
      </DItems>

      {containerState === 'empty' && <NoDocuments />}

      {openAddDocument.get() && (
        <AddDocuments
          manufacturerData={equipment?.product?.manufacturer}
          productData={equipment?.product}
          openAddDocument={openAddDocument}
          onSuccess={() => {
            queryClient.invalidateQueries([
              'equipments',
              params.equipmentId,
              'documents',
            ]);
          }}
        />
      )}

      {openEditDocument.get() && (
        <EditDocument
          manufacturerData={equipment?.product?.manufacturer}
          productData={equipment?.product}
          openEditDocument={openEditDocument}
          selectedDocument={selectedDocument.get()}
          onSuccess={() => {
            queryClient.invalidateQueries([
              'equipments',
              params.equipmentId,
              'documents',
            ]);
          }}
        />
      )}
    </Container>
  );
};
